/**
 * The three rules `views/Login.tsx` must not break (task-7-brief.md Step 1),
 * extracted so they are pinned by exhaustive pure tests (test/loginFlow.test.ts)
 * rather than a fourth DOM suite -- this plan's DOM-test scope is capped at
 * three and all three are already spoken for. Login.tsx calls these and
 * renders the answer; it decides nothing itself, the same split bootState.ts
 * makes for Boot.tsx.
 *
 * All three are about NOT knowing more than the server said:
 *
 *  1. POST /auth/request answers with ONE generic body for sent / unknown_email
 *     / rate_limited / send_failed (api/src/authRoutes.ts:142-143).
 *     `outcomeForRequestCode` renders that message verbatim and advances to
 *     the code step unconditionally.
 *  2. EMAIL_UNDELIVERABLE (authRoutes.ts:138-141) is the one spec-mandated
 *     exception (spec §4.6.1 pitfall 1): `outcomeForRequestCode` is the only
 *     place `advance` comes back false -- there is no code coming.
 *  3. 401 invalid_code (authRoutes.ts:158-161) has ONE message for every
 *     reason (wrong code, expired, too many attempts, no such request).
 *     `isInvalidCodeError` is the only thing Login.tsx is allowed to branch
 *     on for that response; it must never read the server's own `message`
 *     (which could one day differ per reason) or count attempts client-side.
 */
import { ApiError } from './api';

export interface RequestCodeResponse {
  message: string;
  code?: 'EMAIL_UNDELIVERABLE';
}

export interface RequestStepOutcome {
  /** Rendered verbatim -- never re-worded, never swapped for client copy. */
  message: string;
  /** False only for EMAIL_UNDELIVERABLE: no code is coming, so there is
   *  nothing to advance to. */
  advance: boolean;
}

export function outcomeForRequestCode(response: RequestCodeResponse): RequestStepOutcome {
  return { message: response.message, advance: response.code !== 'EMAIL_UNDELIVERABLE' };
}

/**
 * True only for the exact failure /auth/verify uses to withhold why a code
 * was rejected. Anything else (a network failure, a 5xx, a malformed
 * request) is a real, different failure and Login.tsx falls back to the
 * shell's ordinary generic message for it.
 */
export function isInvalidCodeError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'invalid_code';
}
